/**
 * Own-history thinning and the rules around it (decision 29; sync case table S4, R1; part 07 log, principles 1–5,
 * rules K1–K4, C1, C2, P and table G). Pure: the time is always passed in.
 */
import type { Snapshot } from './records.ts';

export const MINUTE_MS = 60_000;
export const DAY_MS = 86_400_000;

/** How often a changing chapter gets a snapshot (decision 29, "every ~10 min"; table S, S1). */
export const SNAPSHOT_INTERVAL_MS = 10 * MINUTE_MS;

/** How far past its receipt a stated time may lie (decision 29, "plus 5 min"; principle 4). */
export const CLAMP_SLACK_MS = 5 * MINUTE_MS;

/** Every snapshot this young is kept (K1). */
export const RECENT_MS = DAY_MS;

/** How many of a group's most recent days with snapshots keep their last one (K2). */
export const DAILY_DAYS = 30;

/** How long an event snapshot is kept (K4). */
export const EVENT_MS = 30 * DAY_MS;

/** At most this many day and week keepers older than a day per chapter per owner (C1, decision 29). */
export const HISTORY_CAP = 200;

/** At most this many events kept only for being events (C2). */
export const EVENT_CAP = 200;

/**
 * A stated creation time clamped on entry (principle 4): raised to the group's previous time, then lowered to
 * `received + 5 min`; when the two cross (the clock went back after running ahead), the upper bound wins.
 */
export function clampTime(stated: number, previous: number | undefined, received: number): number {
  return Math.min(Math.max(stated, previous ?? stated), received + CLAMP_SLACK_MS);
}

/** The writer's local day of a time, as days since 1970-01-01 in the offset `zone` (minutes east of UTC; principle 3). */
export function localDay(createdAt: number, zone: number): number {
  return Math.floor((createdAt + zone * MINUTE_MS) / DAY_MS);
}

/** The ISO week (Monday first) of a local day: 1970-01-01 was a Thursday, so weeks turn on days 4, 11, … */
export function localWeek(day: number): number {
  return Math.floor((day + 3) / 7);
}

/** How long a DOCX export keeps its base pinned (decision 64; part 22, PN4): twelve calendar months. */
export const EXPORT_PIN_MONTHS = 12;

/** PN4: when a DOCX export made at `moment` lets its base go: the same moment twelve calendar months on, in UTC. */
export function exportPinUntil(moment: number): number {
  const date = new Date(moment);
  date.setUTCMonth(date.getUTCMonth() + EXPORT_PIN_MONTHS);
  return date.getTime();
}

/** Whether a pin keeps `snapshot` out of thinning at `now` (rule P). */
export function isPinned(snapshot: Snapshot, now: number): boolean {
  return snapshot.pins.some((pin) => pin.kind === 'share-link' || pin.until > now);
}

/**
 * Whether a chapter that `changed` since its owner's last snapshot is due another (S1): it has none, ten minutes have
 * passed since the last, or the last lies more than the clamp's slack ahead of `now` (a clock that ran ahead and was
 * fixed; otherwise no snapshot would come until real time caught up).
 */
export function snapshotDue(last: Pick<Snapshot, 'createdAt'> | undefined, now: number, changed: boolean): boolean {
  if (!changed) return false;
  if (last === undefined) return true;
  return now - last.createdAt >= SNAPSHOT_INTERVAL_MS || last.createdAt - now > CLAMP_SLACK_MS;
}

/** Whether `a` came after `b` in its group: higher `seq`, then higher ID (principle 2; the ID makes it total). */
export function later(a: Pick<Snapshot, 'seq' | 'id'>, b: Pick<Snapshot, 'seq' | 'id'>): boolean {
  return a.seq === b.seq ? a.id > b.id : a.seq > b.seq;
}

/** A snapshot with its local day and week. */
interface Entry {
  readonly snapshot: Snapshot;
  readonly day: number;
  readonly week: number;
}

/** Whether `a` outranks `b` for the last place of a day or week: the later stamp, then {@link later} (principle 2). */
function outranks(a: Entry, b: Entry): boolean {
  return a.snapshot.createdAt === b.snapshot.createdAt ? later(a.snapshot, b.snapshot) : a.snapshot.createdAt > b.snapshot.createdAt;
}

/** The order the caps evict in: the oldest first. */
const evictionOrder = (a: Entry, b: Entry): number => (outranks(a, b) ? 1 : -1);

/** The entry of each key that outranks the others. */
function lastBy(entries: readonly Entry[], key: (entry: Entry) => number): Map<number, Entry> {
  const last = new Map<number, Entry>();
  for (const entry of entries) {
    const current = last.get(key(entry));
    if (current === undefined || outranks(entry, current)) last.set(key(entry), entry);
  }
  return last;
}

/** Whether a snapshot is stamped a day or more ahead of `now` (rule D). */
const isDeferred = (snapshot: Snapshot, now: number): boolean => snapshot.createdAt - now >= RECENT_MS;

/**
 * The snapshots of `group` stamped more than the clamp's slack before one made earlier (by {@link later}) that isn't
 * deferred (rule S): where the clock disagreed with the history. Each maps to the latest stamp of anything made before
 * it, deferred ones included: until `now` reaches that, the clock may still be the wrong one, so it isn't settled yet.
 */
function outOfOrder(group: readonly Snapshot[], now: number): Map<Snapshot, number> {
  const found = new Map<Snapshot, number>();
  let highest = -Infinity;
  let latest = -Infinity;
  for (const snapshot of [...group].sort((a, b) => (later(a, b) ? 1 : -1))) {
    if (snapshot.createdAt < highest - CLAMP_SLACK_MS) found.set(snapshot, latest);
    latest = Math.max(latest, snapshot.createdAt);
    if (!isDeferred(snapshot, now)) highest = Math.max(highest, snapshot.createdAt);
  }
  return found;
}

/** The IDs of `group`'s unpinned snapshots that no rule keeps at `now`. */
function thinGroup(group: readonly Snapshot[], now: number): string[] {
  const entries = group.map((snapshot): Entry => {
    const day = localDay(snapshot.createdAt, snapshot.zone);
    return { snapshot, day, week: localWeek(day) };
  });
  // S: K1 and K4 age a snapshot from when it was settled; one out of order and not settled yet counts from now.
  const unsettled = outOfOrder(group, now);
  const agedFrom = (entry: Entry): number => entry.snapshot.settled ?? (unsettled.has(entry.snapshot) ? now : entry.snapshot.createdAt);
  // K0: the group's newest (by creation) is always kept.
  const newest = entries.reduce<Entry | undefined>((a, b) => (a === undefined || later(b.snapshot, a.snapshot) ? b : a), undefined);
  // D: stamped a day or more ahead of now, a snapshot is junk from a clock that ran ahead or real history seen by a
  // clock that is now behind; the device can't tell, so it is kept and left out of every rule until its time comes.
  const deferred = (entry: Entry): boolean => isDeferred(entry.snapshot, now);
  const present = entries.filter((entry) => !deferred(entry));
  const lastOfDay = lastBy(present, (entry) => entry.day);
  const lastOfWeek = lastBy(present, (entry) => entry.week);
  // K2 counts days with a snapshot no later than now, most recent first.
  const past = present.filter((entry) => entry.snapshot.createdAt <= now).map((entry) => entry.day);
  const recentDays = new Set([...new Set(past)].sort((a, b) => b - a).slice(0, DAILY_DAYS));
  const recent = (entry: Entry): boolean => Math.abs(now - agedFrom(entry)) < RECENT_MS;
  const dayKeeper = (entry: Entry): boolean => lastOfDay.get(entry.day) === entry && recentDays.has(entry.day);
  const weekKeeper = (entry: Entry): boolean => lastOfWeek.get(entry.week) === entry;
  const event = (entry: Entry): boolean => entry.snapshot.reason !== 'interval' && now - agedFrom(entry) < EVENT_MS;

  const old = present.filter((entry) => !recent(entry));
  const counted = old.filter(dayKeeper).length;
  const weeklyOnly = old.filter((entry) => weekKeeper(entry) && !dayKeeper(entry) && !event(entry)).sort(evictionOrder);
  const eventsOnly = old.filter((entry) => event(entry) && !dayKeeper(entry) && !weekKeeper(entry)).sort(evictionOrder);
  const evicted = new Set([
    ...weeklyOnly.slice(0, Math.max(0, counted + weeklyOnly.length - HISTORY_CAP)),
    ...eventsOnly.slice(0, Math.max(0, eventsOnly.length - EVENT_CAP)),
  ]);
  const kept = (entry: Entry): boolean =>
    entry === newest ||
    deferred(entry) ||
    (!evicted.has(entry) && (recent(entry) || dayKeeper(entry) || weekKeeper(entry) || event(entry)));
  return entries.filter((entry) => !kept(entry)).map((entry) => entry.snapshot.id);
}

/**
 * The IDs of the unpinned snapshots thinning finds out of order at `now`, with no `settled` yet, once `now` has
 * reached the latest stamp of everything made before them (rule S): the clock that made them was behind, and is right
 * again. The thinning write records `settled = now` on each; until then each counts as made now.
 */
export function toSettle(snapshots: readonly Snapshot[], now: number): Set<string> {
  const found = new Set<string>();
  for (const group of groups(snapshots, now).values()) {
    for (const [snapshot, latest] of outOfOrder(group, now)) if (snapshot.settled === null && latest <= now) found.add(snapshot.id);
  }
  return found;
}

/** The unpinned snapshots, by chapter and owner (principle 1). */
function groups(snapshots: readonly Snapshot[], now: number): Map<string, Snapshot[]> {
  const byGroup = new Map<string, Snapshot[]>();
  for (const snapshot of snapshots) {
    if (isPinned(snapshot, now)) continue;
    const key = `${snapshot.chapterId}/${snapshot.owner}`;
    const group = byGroup.get(key) ?? [];
    group.push(snapshot);
    byGroup.set(key, group);
  }
  return byGroup;
}

/**
 * The IDs of the snapshots thinning removes at `now` (part 07 log, rules K1–K4, C1, C2, P): pinned ones are left
 * alone, and every chapter and owner is thinned apart (principle 1). A function of the snapshots and `now` only:
 * the relay's listing never enters it (principle 5). Idempotent, and the order of `snapshots` doesn't matter.
 */
export function thin(snapshots: readonly Snapshot[], now: number): Set<string> {
  return new Set([...groups(snapshots, now).values()].flatMap((group) => thinGroup(group, now)));
}
